import React, { useCallback, useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import LeadsContext from '../../../../providers/Leads';
import ListingContext from '../../../../providers/Listing/Listing.context';
import MemberFileAccessSelector from 'router/subrouters/DataRoom/components/MemberFileAccessSelector';
import ReleaseOrderNote from 'router/subrouters/DataRoom/components/MemberFileAccessSelector/components/ReleaseOrderNote';
import { useTranslation } from 'react-i18next';
import WithdrawnLeadsFileAccessSnapshot from './components/WithdrawnLeadsFileAccessSnapshot';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { ReleaseOrderSharePreview } from 'router/subrouters/DataRoom/models/DataRoomBulk';
import useRefetch from 'hooks/useRefetch';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import {
  AUTOMATION_REMOVAL_SHARED_FROM,
  AutomationRemovalCancelledEventProps,
} from 'models/MixpanelEvents';
import { getSellingPortfolio } from 'utils';

import './LeadFiles.styles.scss';

type LeadFilesProps = {
  className?: string;
};

const LeadFiles: React.FC<LeadFilesProps> = (props) => {
  const { className } = props;

  const { selectedLead: lead, updateLead } = useContext(LeadsContext);

  const {
    canManageDataRoom,
    dataRoomMemberId,
    withdrawnInterest,
    firstName,
    buyerStep,
  } = lead || {};

  const {
    canAccessDataRoom,
    areActionsDisabled,
    dealRoom,
    dealRoom: { dataRoomId, filesSort },
  } = useContext(ListingContext);

  const { t } = useTranslation();

  const { refetchFlag, refetch } = useRefetch();

  const getMixpanelProps = useGetMixpanelProps();

  const classes = classNames('lead-files', className);

  const releaseOrderNote = useMemo(
    () => (
      <ReleaseOrderNote
        name={firstName}
        automationLink={
          <Link
            to={`${getSellingPortfolio(dealRoom?._id, 'settings')}${
              buyerStep ? `/${buyerStep}` : ''
            }`}
          />
        }
      />
    ),
    [firstName, buyerStep, dealRoom?._id],
  );

  const onAutomationRemovalCancelled = useCallback(
    (leads: ReleaseOrderSharePreview['leads']) =>
      mixpanelService.automationRemovalCancelled(
        getMixpanelProps({
          dealId: dealRoom?._id,
          dealName: dealRoom?.name,
          leadIds: leads.map(({ _id }) => _id),
          leadNames: leads.map(({ name }) => name),
          sharedFrom: AUTOMATION_REMOVAL_SHARED_FROM.LEAD_DRAWER,
        }) as AutomationRemovalCancelledEventProps,
      ),
    [dealRoom?._id, dealRoom?.name, getMixpanelProps],
  );

  const updateMember = useCallback(
    (member: DataRoomMember, isRemovedFromAutomation?: boolean) => {
      updateLead({
        ...lead,
        numberOfAccessableDocuments: member.accessableDataRoomNodesView.length,
        // Out of automation, a paused order has nothing left to unlock.
        ...(isRemovedFromAutomation && {
          isReleaseOrderPaused: false,
          isReleaseOrderAwaitingReaction: false,
        }),
      });
    },
    [lead, updateLead],
  );

  const Content = useMemo(() => {
    if (!dataRoomMemberId) {
      if (withdrawnInterest) {
        return <WithdrawnLeadsFileAccessSnapshot />;
      }

      return <div className="white-panel">{t('leadIsNotDataRoomMember')}</div>;
    }

    if (!canManageDataRoom || !canAccessDataRoom) {
      return (
        <div className="white-panel">
          {t('youDontHavePermissionToChangeLeadFileAccess')}
        </div>
      );
    }

    if (!filesSort) {
      return <div className="white-panel">{t('noFilesInDR')}</div>;
    }

    return (
      <MemberFileAccessSelector
        defaultOpen
        refetchFlag={refetchFlag}
        dataRoomId={dataRoomId}
        dataRoomMemberId={dataRoomMemberId}
        isDisabled={areActionsDisabled}
        leadStagesCopies={dealRoom?.leadStagesCopies}
        releaseOrderNote={releaseOrderNote}
        updateMember={updateMember}
        refetch={refetch}
        onAutomationRemovalCancelled={onAutomationRemovalCancelled}
      />
    );
  }, [
    dataRoomMemberId,
    canManageDataRoom,
    canAccessDataRoom,
    refetchFlag,
    dataRoomId,
    areActionsDisabled,
    withdrawnInterest,
    filesSort,
    dealRoom?.leadStagesCopies,
    releaseOrderNote,
    t,
    updateMember,
    refetch,
    onAutomationRemovalCancelled,
  ]);

  return <div className={classes}>{Content}</div>;
};

export default LeadFiles;
